import type { FC, ReactElement } from 'react';

import type { InputSpinnerTheme } from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import testId from '@snowit/utils/testId';

import Typo from '../../Typography/Typo';
import Spinner from '../Spinner';

import cx from 'classnames';
import s from './InputSpinner.module.scss';

export interface IInputSpinner {
  theme?: InputSpinnerTheme;
  label?: string;
  sublabel?: string;
  error?: string;
  value: number;
  maxValue?: number;
  minValue?: number;
  name?: string;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  onChange: (value: number) => void;
  className?: string;
  style?: {};
  /** Jest/Cypress test string */
  test?: string;
}

const InputSpinner: FC<IInputSpinner> = (props: IInputSpinner): ReactElement => {
  const {
    theme = 'default',
    label,
    sublabel,
    error,
    value,
    maxValue,
    minValue,
    name,
    readOnly,
    required,
    disabled,
    onChange,
    className,
    test,
  } = props;
  const a11yH = useA11y();

  const id = a11yH.makeId(props.id);
  const hintId = a11yH.makeId(props.id, 'hint');

  const renderLabel = () => {
    if (!label) return null;
    return (
      <div className={s.wrapLabel}>
        <Typo text={label} tag={'span'} size={'df'} color={'text'} a11y={{ htmlFor: id }} />
        {sublabel && <Typo id={hintId} text={sublabel} tag={'span'} size={'sm'} color={'text-l'} />}
      </div>
    );
  };

  return (
    <div
      className={cx(s.wrapper, className, {
        [s[theme]]: true,
        [s.disabled]: disabled,
        [s.error]: error,
        [s.readOnly]: readOnly,
        [s.required]: required,
      })}
      id={id}
      {...testId('input_spinner_wrapper', test)}
    >
      {renderLabel()}
      <Spinner
        className={s.spinner}
        id={id}
        name={name}
        value={value}
        maxValue={maxValue}
        minValue={minValue}
        disabled={disabled}
        onChange={(newValue) => onChange(newValue)}
        a11y={{ describedBy: sublabel ? hintId : undefined }}
        test={`${test}_input_spinner`}
      />
    </div>
  );
};

export default InputSpinner;
